import { date } from '@eti/utils';
import { useDateFormat, useSiteContext, useTranslation } from '@eti/providers';
import { useMediaQuery } from 'usehooks-ts';
import { breakpoints } from '@etg/wings';
import type { BoundAsSegment } from '../model';
import type { CodeAndNameType } from '../../../../../../models';
import { useBrandedFareName } from '../../../../../../../../common/custom-hooks/useBrandedFareName';
import { TRAIN_SEGMENTS, type TRAIN_SEGMENT_TYPE } from '../../../constants';
import { getTimeLabel } from '../../../../../../../../common/time/utils/time-utils';
import { getDurationLabel } from '../../../../../../../../common/time/utils/duration-utils';

export const useGenerateTripBoundAriaLabel = (
  boundAsSegment: BoundAsSegment,
  marketingCarriers: CodeAndNameType[],
  title: string,
  includesTrainSegments: TRAIN_SEGMENT_TYPE,
  numberOfTechnicalStops?: number,
) => {
  const { t } = useTranslation();
  const { day } = useDateFormat();
  const { language } = useSiteContext();
  const isMobile = useMediaQuery(`(max-width: ${breakpoints._768})`);
  const {
    arrivedAt,
    brandedFareInformation,
    cabinClassName,
    departuredAt,
    destination,
    duration,
    marketingCarrier,
    origin,
    stops,
    isSelfTransfer,
  } = boundAsSegment;
  const brandedFareInfoAndMarketingCarrier = { brandedFareInformation, marketingCarrier };
  const brandedFareName = useBrandedFareName(brandedFareInfoAndMarketingCarrier);
  const dayOfDeparture = date.format(departuredAt, day);
  const timeOfDeparture = getTimeLabel(language.locale, departuredAt, 'time');
  const timeOfArrival = getTimeLabel(language.locale, arrivedAt, 'time');
  const tripDuration = getDurationLabel(language.locale, duration);
  const durationInDays = date.durationInDays(new Date(departuredAt), new Date(arrivedAt));
  const durationInDaysText =
    durationInDays > 0
      ? `,${t('ScreenReader.Result.ResultCard.TripBound.ArrivalDays.Text', durationInDays)}`
      : '';
  const originAirportCode = origin.airportCode ?? origin.code;
  const destinationAirportCode = destination.airportCode ?? destination.code;
  const stopText =
    stops === 1 ? t('TripDetails.Details.Stop') : t('TripDetails.Details.Stops', stops);
  const directFlightOrStopsText = stops === 0 ? t('TripDetails.Details.Direct') : stopText;
  const carriersText = marketingCarriers.map(({ name }) => name).join(',');
  const selfTransferText = isSelfTransfer
    ? `,${t('ScreenReader.Result.ResultCard.TripBound.TripEvent.Text')} ${t(
        'TripDetails.Details.SelfTransfer',
      )}`
    : '';
  const partByTrainText =
    includesTrainSegments === TRAIN_SEGMENTS.SOME || includesTrainSegments === TRAIN_SEGMENTS.ALL
      ? `,${t('IbeClient.Segment.PartByTrain.Text')}`
      : '';
  const technicalStopsText =
    numberOfTechnicalStops && numberOfTechnicalStops > 0
      ? `,${t('TripDetails.Details.NumberOfTechnicalStops', numberOfTechnicalStops)}`
      : '';

  if (isMobile) {
    return `${title},${t(
      'ScreenReader.Result.ResultCard.TripBound.Carriers.Text',
    )} ${carriersText},${t(
      'ScreenReader.Result.ResultCard.TripBound.DepartureTime.Text',
    )} ${timeOfDeparture},${originAirportCode} ${origin.cityName},${t(
      'ScreenReader.Result.ResultCard.TripBound.ArrivalTime.Text',
    )} ${timeOfArrival}${durationInDaysText},${destinationAirportCode} ${
      destination.cityName
    },${directFlightOrStopsText},${t(
      'ScreenReader.Result.ResultCard.TripBound.TravelDuration.Text',
    )} ${tripDuration}${partByTrainText}${technicalStopsText}${selfTransferText}`;
  }

  return `${title},${dayOfDeparture},${t(
    'ScreenReader.Result.ResultCard.TripBound.CabinClass.Text',
  )}${brandedFareName || t(`IbeClient.CabinClass.${cabinClassName}.Text`)},${t(
    'ScreenReader.Result.ResultCard.TripBound.DepartureTime.Text',
  )} ${timeOfDeparture},${originAirportCode} ${origin.cityName},${t(
    'ScreenReader.Result.ResultCard.TripBound.TravelDuration.Text',
  )} ${tripDuration},${directFlightOrStopsText}${partByTrainText}${technicalStopsText},${t(
    'ScreenReader.Result.ResultCard.TripBound.ArrivalTime.Text',
  )} ${timeOfArrival}${durationInDaysText},${destinationAirportCode} ${destination.cityName},${t(
    'ScreenReader.Result.ResultCard.TripBound.Carriers.Text',
  )} ${carriersText}${selfTransferText}`;
};

export default useGenerateTripBoundAriaLabel;
